import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/* Motion Guidelines:
   - translateY(-1px) on hover
   - darken bg 6% on hover  
   - shadow increase 30% on hover
   - translateY(0) on active
   - 220ms ease-out transition
   - No scale, no glow, no ripple
*/
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-bold ring-offset-background transition-all duration-[220ms] ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 uppercase tracking-wide",
  {
    variants: {
      variant: {
        default: "bg-accent-green text-primary-text hover:bg-[#5f8363] rounded-2xl shadow-button hover:shadow-premium-lg hover:-translate-y-px active:translate-y-0 active:shadow-button",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-2xl shadow-button hover:-translate-y-px active:translate-y-0",
        outline: "border border-primary-text/20 bg-transparent hover:bg-primary-text/5 hover:border-primary-text/40 rounded-2xl text-primary-text hover:-translate-y-px active:translate-y-0",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-2xl border border-primary-text/10 hover:-translate-y-px active:translate-y-0",
        ghost: "hover:bg-accent-green/10 hover:text-accent-green rounded-2xl text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-14 px-8 py-4 text-sm",
        sm: "h-10 px-4 text-xs",
        lg: "h-16 px-10 text-base",
        icon: "h-14 w-14 rounded-2xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
